"use client";

import { useState } from "react";

import { Popover, Text, UnstyledButton } from "@mantine/core";

import classes from "./Hero.module.css";

/** The four things a company holds, each with the plain question it answers. */
const OPERATING_MODEL_PARTS = [
  { question: "What we are aiming at", part: "Strategy" },
  { question: "Who decides what", part: "Decision rights" },
  { question: "What we hold to", part: "Principles" },
  { question: "What we have settled", part: "Decisions" },
] as const;

/**
 * "operating model" in the hero's line, defined in place. The definition opens
 * on hover, on keyboard focus and on a tap or click, which pins it open until
 * the reader taps elsewhere, moves focus on, or presses Escape.
 */
export default function OperatingModelTerm() {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isPinned, setIsPinned] = useState(false);

  const dismiss = () => {
    setIsHovered(false);
    setIsFocused(false);
    setIsPinned(false);
  };

  return (
    <Popover
      opened={isHovered || isFocused || isPinned}
      onDismiss={dismiss}
      position="bottom-start"
      offset={14}
      width="min(400px, calc(100vw - 32px))"
      // On a phone the card is shifted into view; keep it off the page's edges.
      middlewares={{ shift: { padding: 16 } }}
      withArrow
      arrowSize={14}
      arrowOffset={40}
      arrowPosition="side"
      radius={12}
      transitionProps={{ transition: "pop-top-left", duration: 150 }}
      classNames={{ dropdown: classes.definition }}
    >
      <Popover.Target>
        <UnstyledButton
          className={classes.term}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          // Keyboard focus opens it; the focus a click leaves behind does not,
          // so a second click can close what the first one pinned.
          onFocus={(event) => setIsFocused(event.currentTarget.matches(":focus-visible"))}
          // A pin lasts while the term holds focus, so tabbing on never leaves
          // the card covering the control that took focus.
          onBlur={dismiss}
          onClick={() => setIsPinned((isOpen) => !isOpen)}
          // Mantine closes on Escape only from inside the dropdown; focus
          // stays here on the term.
          onKeyDown={(event) => {
            if (event.key === "Escape") dismiss();
          }}
        >
          operating model
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown>
        <Text className={classes.definitionKicker}>Operating model</Text>
        <Text fz={14} c="gray.7" mb="sm">
          The four things a company holds, kept in one place that people and agents both work from.
        </Text>
        {OPERATING_MODEL_PARTS.map(({ question, part }) => (
          <div className={classes.definitionRow} key={part}>
            <span className={classes.definitionQuestion}>{question}</span>
            <span>{part}</span>
          </div>
        ))}
      </Popover.Dropdown>
    </Popover>
  );
}
